'use client'

import React, {useState, useMemo} from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import {Slot} from '@radix-ui/react-slot'
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    FormProvider,
    useFormContext,
    ControllerRenderProps
} from 'react-hook-form'
import SvgTooltipDanderSolid from 'hec/assets/icons/tooltip-dander-solid'
import TooltipIcon from 'hec/assets/icons/tooltip'

import {cn} from '@/lib/utils'
import {Label} from '@/components/ui/label'
import {Input} from '@/components/ui/input'
import {Select} from '@/components/ui/select'
import {Checkbox} from '@/components/ui/checkbox'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import {PasswordInput} from '@/components/input/PasswordInput'
import useMask from '@react-input/mask/useMask'
import type {MaskOptions} from '@react-input/mask'

const Form = FormProvider

type FormFieldContextValue<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue)

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    ...props
}: ControllerProps<TFieldValues, TName> & {'data-testid'?: string}) => {
    const {'data-testid': dataTestId, ...restProps} = props
    return (
        <FormFieldContext.Provider value={{name: props.name}}>
            <Controller {...restProps} data-testid={dataTestId || 'form-Controller'} />
        </FormFieldContext.Provider>
    )
}

const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext)
    const itemContext = React.useContext(FormItemContext)
    const {getFieldState, formState} = useFormContext()

    const fieldState = getFieldState(fieldContext.name, formState)

    if (!fieldContext) {
        throw new Error('useFormField should be used within <FormField>')
    }

    const {id} = itemContext

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState
    }
}

type FormItemContextValue = {
    id: string
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue)

interface ExtendedHTMLAttributes extends React.HTMLAttributes<HTMLDivElement> {
    'data-testid'?: string | undefined
}

const FormItem = React.forwardRef<HTMLDivElement, ExtendedHTMLAttributes>(
    ({className, 'data-testid': dataTestId, ...props}, ref) => {
        const id = React.useId()

        return (
            <FormItemContext.Provider value={{id}}>
                <div
                    ref={ref}
                    className={cn('space-y-2', className)}
                    data-testid={dataTestId || 'form-FormItem'}
                    {...props}
                />
            </FormItemContext.Provider>
        )
    }
)
FormItem.displayName = 'FormItem'

type FormLabelProps = {
    className?: string
    'data-testid'?: string | undefined
}

const FormLabel = React.forwardRef<
    React.ElementRef<typeof LabelPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & FormLabelProps
>(({className, 'data-testid': dataTestId, ...props}, ref) => {
    const {error, formItemId} = useFormField()

    return (
        <Label
            ref={ref}
            className={cn(error && 'text-destructive', className)}
            htmlFor={formItemId}
            {...props}
            data-testid={dataTestId || 'form-Label'}
        />
    )
})
FormLabel.displayName = 'FormLabel'

const FormControl = React.forwardRef<
    React.ElementRef<typeof Slot>,
    React.ComponentPropsWithoutRef<typeof Slot> & {'data-testid'?: string}
>(({'data-testid': dataTestId, ...props}, ref) => {
    const {error, formItemId, formDescriptionId, formMessageId} = useFormField()

    return (
        <Slot
            ref={ref}
            id={formItemId}
            aria-describedby={
                !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`
            }
            aria-invalid={!!error}
            {...props}
            data-testid={dataTestId || 'form-Slot'}
        />
    )
})
FormControl.displayName = 'FormControl'

interface ExtendedParagraphAttributes extends React.HTMLAttributes<HTMLParagraphElement> {
    'data-testid'?: string | undefined
}

const FormDescription = React.forwardRef<HTMLParagraphElement, ExtendedParagraphAttributes>(
    ({className, 'data-testid': dataTestId, ...props}, ref) => {
        const {formDescriptionId} = useFormField()

        return (
            <p
                ref={ref}
                id={formDescriptionId}
                className={cn('text-sm text-muted-foreground', className)}
                data-testid={dataTestId || 'form-FormDescription'}
                {...props}
            />
        )
    }
)
FormDescription.displayName = 'FormDescription'

const FormMessage = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement> & {'data-testid'?: string | undefined}
>(
    (
        {className, children, 'data-testid': dataTestId, ...props}: ExtendedParagraphAttributes,
        ref
    ) => {
        const {error, formMessageId} = useFormField()
        const body = error ? String(error?.message) : children

        if (!body) {
            return null
        }

        return (
            <p
                ref={ref}
                id={formMessageId}
                className={cn(
                    'px-2 pt-2 text-right body-md lg:text-sm leading-normal italic',
                    error && 'text-error',
                    className
                )}
                data-testid={dataTestId || 'form-FormMessage'}
                {...props}
            >
                {body}
                {error && (
                    <SvgTooltipDanderSolid className="w-[1.125rem] h-[1.125rem] ml-[0.3125rem] mb-[0.1875rem] inline-block" />
                )}
            </p>
        )
    }
)
FormMessage.displayName = 'FormMessage'

type InputType = React.InputHTMLAttributes<HTMLInputElement>['type']

type FormInputProps = {
    label: string
    inputType?: InputType
    className?: string
    validate?: (() => boolean) | (() => Promise<boolean>)
    required?: boolean
    tooltipMsg?: string | React.ReactNode
    fieldProps: ControllerRenderProps<any, any> // eslint-disable-line
    'data-testid'?: string | undefined
} & Omit<React.ComponentProps<typeof Input>, 'type'>

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
    (
        {
            label,
            inputType = 'text',
            className,
            required = false,
            tooltipMsg,
            validate = () => true,
            'data-testid': dataTestId,
            fieldProps,
            ...rest
        },
        ref
    ) => {
        const [active, setActive] = useState(false)
        const [isValid, setIsValid] = useState(false)
        const {value: inputValue, onBlur, ...restFieldProps} = fieldProps
        const isEmpty = useMemo(() => !inputValue, [inputValue])

        const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setTimeout(() => {
                e.target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }, 300)
        }

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (onFocus) onFocus(e)
            setActive(true)
        }

        const handleBlur = async () => {
            onBlur()
            setActive(false)

            let valid = true
            const validationResult = validate()
            if (validationResult instanceof Promise) {
                valid = await validationResult // Wait for the promise to resolve
            } else {
                valid = validationResult // Directly use the returned value if it's synchronous
            }
            setIsValid(valid)
        }

        return (
            <FormItem
                className={cn('relative grow flex flex-col space-y-0 text-foreground', className)}
                data-testid={dataTestId || 'form-FormItem'}
            >
                <FormLabel
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(
                        'absolute px-2.5 top-[0.9375rem] eyebrow-sm z-[1] flex items-center space-x-1',
                        {
                            'top-2 eyebrow-sm': active || !isEmpty,
                            'eyebrow-common text-xs leading-5 tracking-wide': !active && isEmpty
                        }
                    )}
                    data-testid={dataTestId || 'form-FormLabel'}
                >
                    <span>{label}</span>
                    {!required && (
                        <em className="capitalize body-sm lg:text-xs lg:leading-3">(Optional)</em>
                    )}
                    {tooltipMsg && (
                        <TooltipProvider data-testid={dataTestId || 'form-TooltipProvider'}>
                            <Tooltip data-testid={dataTestId || 'form-Tooltip'}>
                                <TooltipTrigger
                                    className="mb-1"
                                    data-testid={dataTestId || 'form-TooltipTrigger'}
                                >
                                    <TooltipIcon />
                                </TooltipTrigger>
                                <TooltipContent data-testid={dataTestId || 'form-TooltipContent'}>
                                    {typeof tooltipMsg === 'string' ? (
                                        <div dangerouslySetInnerHTML={{__html: tooltipMsg}} />
                                    ) : (
                                        tooltipMsg
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </FormLabel>
                <FormControl data-testid={dataTestId || 'form-FormControl'} ref={ref}>
                    {inputType === 'password' ? (
                        <PasswordInput
                            type={inputType}
                            showValidCheck={isValid && required}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            {...restFieldProps}
                            data-testid={dataTestId || 'form-Input'}
                        />
                    ) : (
                        <Input
                            type={inputType}
                            showValidCheck={isValid && required}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            {...rest}
                            {...restFieldProps}
                            data-testid={dataTestId || 'form-Input'}
                        />
                    )}
                </FormControl>
                <FormMessage data-testid={dataTestId || 'form-FormMessage'} />
            </FormItem>
        )
    }
)
FormInput.displayName = 'FormInput'

/**
 *
 * @param mask - The *default* input mask is "(___) \_\_\_\-\_\_\_\_".
 * @param replacement - The *default* replacement pattern is {_: /\d/}.
 * @returns
 */
const FormPhone: React.FC<Omit<FormInputProps, 'inputType' | 'ref'> & MaskOptions> = ({
    /**
     * The input mask to apply, default '(___) ___-____'.
     */
    mask = '(___) ___-____',
    replacement = {_: /\d/},
    showMask,
    separate,
    track,
    modify,
    'data-testid': dataTestId,
    ...props
}) => {
    const inputRef = useMask({
        mask,
        replacement,
        showMask,
        separate,
        track,
        modify
    })
    return <FormInput {...props} ref={inputRef} data-testid={dataTestId || 'form-FormInput'} />
}
FormPhone.displayName = 'FormPhone'

type FormSelectProps = {
    label: string
    className?: string
    children: React.ReactNode
    required?: boolean
    tooltipMsg?: string | React.ReactNode
    fieldProps: ControllerRenderProps<any, any> // eslint-disable-line
    'data-testid'?: string | undefined
}
const FormSelect: React.FC<FormSelectProps> = ({
    label,
    className,
    children,
    required = false,
    tooltipMsg,
    fieldProps,
    'data-testid': dataTestId
}) => {
    const {value: selectValue, ...restFieldProps} = fieldProps
    const isEmpty = useMemo(() => !selectValue, [selectValue])

    return (
        <FormItem
            className={cn('relative grow flex flex-col space-y-0 text-foreground', className)}
            data-testid={dataTestId || 'form-FormItem'}
        >
            <FormLabel
                className={cn(
                    'absolute px-2.5 top-[0.9375rem] eyebrow-sm z-0 flex items-center space-x-1',
                    {
                        'top-2 eyebrow-sm': !isEmpty,
                        'eyebrow-common text-xs leading-5 tracking-wide': isEmpty
                    }
                )}
                data-testid={dataTestId || 'form-FormLabel'}
            >
                <span>{label}</span>
                {!required && (
                    <em className="capitalize body-sm lg:text-xs lg:leading-3">(Optional)</em>
                )}
                {tooltipMsg && (
                    <TooltipProvider data-testid={dataTestId || 'form-TooltipProvider'}>
                        <Tooltip data-testid={dataTestId || 'form-Tooltip'}>
                            <TooltipTrigger
                                className="mb-1"
                                data-testid={dataTestId || 'form-TooltipTrigger'}
                            >
                                <TooltipIcon />
                            </TooltipTrigger>
                            <TooltipContent data-testid={dataTestId || 'form-TooltipContent'}>
                                {typeof tooltipMsg === 'string' ? (
                                    <div dangerouslySetInnerHTML={{__html: tooltipMsg}} />
                                ) : (
                                    tooltipMsg
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </FormLabel>
            <FormControl data-testid={dataTestId || 'form-FormControl'}>
                <Select {...restFieldProps} data-testid={dataTestId || 'form-Select'}>
                    {children}
                </Select>
            </FormControl>
            <FormMessage data-testid={dataTestId || 'form-FormMessage'} />
        </FormItem>
    )
}
FormSelect.displayName = 'FormSelect'

type FormCheckboxProps = {
    label: string | React.ReactNode
    className?: string
    fieldProps: ControllerRenderProps<any, any> // eslint-disable-line
    onCheckedChange?: (checked: boolean | string) => void
    'data-testid'?: string | undefined
}

const FormCheckbox: React.FC<FormCheckboxProps> = ({
    label,
    className,
    fieldProps,
    onCheckedChange = () => {},
    'data-testid': dataTestId
}) => {
    const {value: checked, onChange} = fieldProps

    return (
        <FormItem
            className={cn('space-y-0', className)}
            data-testid={dataTestId || 'form-FormItem'}
        >
            <div className="flex flex-row items-start space-x-4 space-y-0">
                <FormControl
                    className="[&[aria-invalid=true]]:border-error"
                    data-testid={dataTestId || 'form-FormControl'}
                >
                    <Checkbox
                        checked={checked}
                        onCheckedChange={(checked) => {
                            onCheckedChange(checked)
                            onChange(checked)
                        }}
                        data-testid={dataTestId || 'form-Checkbox'}
                    />
                </FormControl>
                <div className="space-y-1 leading-none">
                    <FormLabel
                        className="body-md lg:text-sm lg:leading-[1.375rem]"
                        data-testid={dataTestId || 'form-FormLabel'}
                    >
                        {label}
                    </FormLabel>
                </div>
            </div>
            <FormMessage
                className="px-0 text-left"
                data-testid={dataTestId || 'form-FormMessage'}
            />
        </FormItem>
    )
}
FormCheckbox.displayName = 'FormCheckbox'

export {
    useFormField,
    Form,
    FormItem,
    FormLabel,
    FormControl,
    FormDescription,
    FormMessage,
    FormField,
    FormInput,
    FormPhone,
    FormSelect,
    FormCheckbox
}
